import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, BookOpen, Beaker, Users, Target, Trash2, Edit, TrendingUp } from "lucide-react";

interface KnowledgeEntry {
  id: string;
  intervention_type: string;
  name: string;
  description: string | null;
  theoretical_basis: string | null;
  applicable_contexts: string[];
  target_psychometric_profiles: Record<string, any>;
  target_network_roles: string[];
  implementation_template: Record<string, any>;
  effectiveness_evidence: any[];
  source_references: string[];
  created_at: string;
}

const INTERVENTION_TYPES = [
  { value: 'norm_nudge', label: 'Norm Nudge', icon: Users },
  { value: 'network_intervention', label: 'Network Intervention', icon: Target },
  { value: 'commitment_device', label: 'Commitment Device', icon: BookOpen },
  { value: 'social_proof', label: 'Social Proof', icon: Users },
  { value: 'consequence_framing', label: 'Consequence Framing', icon: Target },
  { value: 'gamification', label: 'Gamification', icon: TrendingUp },
  { value: 'mentorship', label: 'Mentorship', icon: Users },
];

export default function SuperAdminKnowledgeBase() {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [formData, setFormData] = useState({
    intervention_type: '',
    name: '',
    description: '',
    theoretical_basis: '',
    applicable_contexts: '',
    target_network_roles: '',
    source_references: '',
    message_template: '',
    delivery_channel: 'notification',
    timing: '',
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('behavioral_science_knowledge')
        .select('*')
        .order('intervention_type');

      if (error) throw error;
      setEntries((data as unknown as KnowledgeEntry[]) || []);
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const entryData = {
        intervention_type: formData.intervention_type,
        name: formData.name,
        description: formData.description,
        theoretical_basis: formData.theoretical_basis,
        applicable_contexts: formData.applicable_contexts.split(',').map(s => s.trim()).filter(Boolean),
        target_network_roles: formData.target_network_roles.split(',').map(s => s.trim()).filter(Boolean),
        source_references: formData.source_references.split('\n').map(s => s.trim()).filter(Boolean),
        implementation_template: {
          message_template: formData.message_template,
          delivery_channel: formData.delivery_channel,
          timing: formData.timing,
        },
      };

      if (editingEntry) {
        const { error } = await supabase
          .from('behavioral_science_knowledge')
          .update(entryData)
          .eq('id', editingEntry.id);
        if (error) throw error;
        toast.success('Entry updated');
      } else {
        const { error } = await supabase
          .from('behavioral_science_knowledge')
          .insert(entryData);
        if (error) throw error;
        toast.success('Entry created');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save entry');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this knowledge base entry?')) return;
    
    try {
      const { error } = await supabase
        .from('behavioral_science_knowledge')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleEdit = (entry: KnowledgeEntry) => {
    setEditingEntry(entry);
    setFormData({
      intervention_type: entry.intervention_type,
      name: entry.name,
      description: entry.description || '',
      theoretical_basis: entry.theoretical_basis || '',
      applicable_contexts: (entry.applicable_contexts || []).join(', '),
      target_network_roles: (entry.target_network_roles || []).join(', '),
      source_references: (entry.source_references || []).join('\n'),
      message_template: entry.implementation_template?.message_template || '',
      delivery_channel: entry.implementation_template?.delivery_channel || 'notification',
      timing: entry.implementation_template?.timing || '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      intervention_type: '',
      name: '',
      description: '',
      theoretical_basis: '',
      applicable_contexts: '',
      target_network_roles: '',
      source_references: '',
      message_template: '',
      delivery_channel: 'notification',
      timing: '',
    });
  };

  const getTypeIcon = (type: string) => {
    const found = INTERVENTION_TYPES.find(t => t.value === type);
    return found ? found.icon : BookOpen;
  };

  const getEffectivenessScore = (evidence: any[]) => {
    if (!evidence || evidence.length === 0) return null;
    const avgEffectSize = evidence.reduce((sum, e) => sum + (e.effect_size || 0), 0) / evidence.length;
    return avgEffectSize;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Behavioral Science Knowledge Base</h1>
          <p className="text-muted-foreground">Living repository of intervention strategies and evidence</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Intervention
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEntry ? 'Edit' : 'Add'} Intervention</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <Select value={formData.intervention_type} onValueChange={(v) => setFormData({...formData, intervention_type: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERVENTION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Peer Comparison Nudge"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the intervention..."
                />
              </div>

              <div>
                <label className="text-sm font-medium">Theoretical Basis</label>
                <Textarea 
                  value={formData.theoretical_basis}
                  onChange={(e) => setFormData({...formData, theoretical_basis: e.target.value})}
                  placeholder="e.g., Based on Bicchieri's Social Norms Theory..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Applicable Contexts (comma-separated)</label>
                  <Input 
                    value={formData.applicable_contexts}
                    onChange={(e) => setFormData({...formData, applicable_contexts: e.target.value})}
                    placeholder="e.g., high_risk, post_violation, new_employee"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Target Network Roles (comma-separated)</label>
                  <Input 
                    value={formData.target_network_roles}
                    onChange={(e) => setFormData({...formData, target_network_roles: e.target.value})}
                    placeholder="e.g., champion, bridge, isolate"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Message Template</label>
                <Textarea 
                  value={formData.message_template}
                  onChange={(e) => setFormData({...formData, message_template: e.target.value})}
                  placeholder="e.g., {percentage}% rekan timmu sudah {behavior} hari ini"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Delivery Channel</label>
                  <Select value={formData.delivery_channel} onValueChange={(v) => setFormData({...formData, delivery_channel: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="notification">Notification</SelectItem>
                      <SelectItem value="chat">Chat</SelectItem>
                      <SelectItem value="supervisor_briefing">Supervisor Briefing</SelectItem>
                      <SelectItem value="team_meeting">Team Meeting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Timing</label>
                  <Input 
                    value={formData.timing}
                    onChange={(e) => setFormData({...formData, timing: e.target.value})}
                    placeholder="e.g., shift_start, post_violation"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Source References (one per line)</label>
                <Textarea 
                  value={formData.source_references}
                  onChange={(e) => setFormData({...formData, source_references: e.target.value})}
                  placeholder="Bicchieri, C. (2006). The Grammar of Society&#10;Cialdini, R. B. (2003). Crafting normative messages"
                  rows={3}
                />
              </div>

              <Button onClick={handleSubmit} className="w-full">
                {editingEntry ? 'Update' : 'Create'} Entry
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4">
        {INTERVENTION_TYPES.slice(0, 4).map(type => {
          const count = entries.filter(e => e.intervention_type === type.value).length;
          const Icon = type.icon;
          return (
            <Card key={type.value} className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-sm text-muted-foreground">{type.label}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Knowledge Base Entries */}
      <div className="grid gap-4">
        {entries.map(entry => {
          const Icon = getTypeIcon(entry.intervention_type);
          const effectScore = getEffectivenessScore(entry.effectiveness_evidence);
          
          return (
            <Card key={entry.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{entry.name}</h3>
                      <Badge variant="outline">{entry.intervention_type.replace('_', ' ')}</Badge>
                      {effectScore !== null && (
                        <Badge variant={effectScore > 0.5 ? "default" : effectScore > 0.2 ? "secondary" : "outline"}>
                          Effect: {effectScore.toFixed(2)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground">{entry.description}</p>
                    
                    {entry.theoretical_basis && (
                      <p className="text-sm text-muted-foreground italic">
                        <strong>Theory:</strong> {entry.theoretical_basis}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2 mt-2">
                      {(entry.applicable_contexts || []).map((ctx, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{ctx}</Badge>
                      ))}
                      {(entry.target_network_roles || []).map((role, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{role}</Badge>
                      ))}
                    </div>

                    {entry.source_references && entry.source_references.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground">
                          <strong>Sources:</strong> {entry.source_references.slice(0, 2).join('; ')}
                          {entry.source_references.length > 2 && ` +${entry.source_references.length - 2} more`}
                        </p>
                      </div>
                    )}

                    {entry.effectiveness_evidence && entry.effectiveness_evidence.length > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <Beaker className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {entry.effectiveness_evidence.length} experiment{entry.effectiveness_evidence.length !== 1 ? 's' : ''} conducted
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(entry)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(entry.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
